import { useMutation } from '@apollo/client';
import { InsertCommentDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { InsertCommentTRPCInput } from './useInsertCommentTRPC';
import { useInsertCommentTRPC } from './useInsertCommentTRPC';

/**
 * Flag-gated wrapper (RSP-6496). Both paths return `{ Id }` so
 * `useComments.tinycomments_reply` reads `.Id` regardless of which ran.
 */
export const useInsertComment = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertCommentGraphQL, graphqlState] = useMutation(
    InsertCommentDocument
  );

  const trpcMutation = useInsertCommentTRPC();

  const insertComment = async (
    input: InsertCommentTRPCInput
  ): Promise<{ Id: string }> => {
    if (trpcEnabled) {
      return trpcMutation.insertComment(input);
    }

    const result = await insertCommentGraphQL({
      variables: {
        Content: input.Content,
        ConversationId: input.ConversationId,
      },
    });
    if (!result.data?.insert_comment_one) {
      throw new Error('Failed to insert comment');
    }

    return { Id: result.data.insert_comment_one.Id };
  };

  if (trpcEnabled) {
    return {
      insertComment,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertComment,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
